import type { NotesTreeResponse } from "../../app/services/notes";
import type { LibraryTreeResponse } from "../../app/services/library";
import {
  isClientGroupPosition,
  isPrimaryClientPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
} from "../../utils/memberPositionUtils";

/**
 * プロジェクト内ナビゲーション（ホーム / トークルーム / DM / ノート / ライブラリ）の
 * 共通定義と表示可否判定。
 *
 * 下部バー（BottomAppBarProject）と横断タスクボードのプロジェクト見出し
 * （ProjectNavLinks）が同じ基準で出し分けるため、ここに純粋関数として置く。
 */

export type ProjectNavItemId = "home" | "talkroom" | "dm" | "notes" | "library";

export interface ProjectNavItem {
  id: ProjectNavItemId;
  icon: string;
  label: string;
  /** ProjectWrapper のタブ名（`?tab=`） */
  path: string;
}

// ナビゲーションアイテムの定義（この順で並べる）。
// プロジェクトの設定は右上のプロジェクトメニュー（3 点リーダー）とバナーから開く。
export const PROJECT_NAV_ITEMS: ProjectNavItem[] = [
  { id: "home", icon: "home", label: "ホーム", path: "home" },
  { id: "talkroom", icon: "forum", label: "トークルーム", path: "talkroom" },
  { id: "dm", icon: "mail", label: "DM", path: "dm" },
  { id: "notes", icon: "import_contacts", label: "ノート", path: "notes" },
  { id: "library", icon: "home_storage", label: "ライブラリ", path: "library" },
];

export interface ProjectNavVisibilityInput {
  /** メンバーシップのポジション文字列（CLIENT / REVIEWER / PRODUCER / ...） */
  position: string | null | undefined;
  /** 納品物なし（total_shots_count === 0）。ホームを出さない */
  noDeliverable: boolean;
  /** DM タブへアクセスできるか（useProjectDmAccess の判定） */
  canAccessDm: boolean;
  /** ノートがこのプロジェクトに共有されている（または作成権限がある）か */
  showNotes: boolean;
  /** 共有ライブラリがこのプロジェクトに割り当てられている（または編集権限がある）か */
  showLibrary: boolean;
}

/**
 * ポジション別の基本ナビアイテム（ノート / ライブラリは共有状況で別途追加）。
 * - クライアント責任者（CLIENT）: home / talkroom / dm
 * - 一般クライアント（REVIEWER）: home / talkroom（参加中の DM スレッドがあれば dm も）
 * - プロデューサーグループ（PRODUCER / DIRECTOR / ADMIN）: home / talkroom / dm
 * - SPECIALIST: dm のみ
 */
const getBaseProjectNavItemIds = (
  position: string,
  canAccessDm: boolean,
): ProjectNavItemId[] => {
  if (isSpecialistPosition(position)) return ["dm"];
  if (isProducerGroupPosition(position)) return ["home", "talkroom", "dm"];
  if (isClientGroupPosition(position)) {
    if (isPrimaryClientPosition(position)) return ["home", "talkroom", "dm"];
    return canAccessDm ? ["home", "talkroom", "dm"] : ["home", "talkroom"];
  }
  return [];
};

/**
 * 表示するナビアイテム ID を順序付きで返す。
 * 下部バー（BottomAppBarProject）とタスクボード見出し（ProjectNavLinks）で共用する。
 */
export const getVisibleProjectNavItemIds = ({
  position,
  noDeliverable,
  canAccessDm,
  showNotes,
  showLibrary,
}: ProjectNavVisibilityInput): ProjectNavItemId[] => {
  if (!position) return [];
  return [
    // 納品物なし（チェックポイント0）のプロジェクトはホームを使わず、
    // トークルーム中心のやりとりになるため「ホーム」を出さない。
    ...getBaseProjectNavItemIds(position, canAccessDm).filter(
      (id) => !(noDeliverable && id === "home"),
    ),
    ...(showNotes ? (["notes"] as const) : []),
    ...(showLibrary ? (["library"] as const) : []),
  ];
};

/**
 * ノートの入口を出すか（ノートが 1 件以上ある、または作成・編集権限がある）。
 * useProjectPanelTabs（下部バー / ⌥8）と useProjectNavItems（タスクボード）で共用。
 */
export const canShowNotes = (
  data: NotesTreeResponse["data"] | undefined,
): boolean => {
  if (!data) return false;
  const sections = data.sections ?? [];
  // ノート（カード表示の共有TIPS 含む）が 1 件以上あるセクションが対象。
  const hasLinkedNotes = sections.some((section) => section.notes.length > 0);
  // ノートが 0 件でも、書き込み権限（コントリビューター以上）を持つ
  // セクションがある、または master_admin（セクション作成可）なら入口を
  // 出し、ノートタブから 1 件目を作成できるようにする
  const canCreateNotes =
    (data.can_manage ?? false) ||
    sections.some((section) => section.can_contribute);
  return hasLinkedNotes || canCreateNotes;
};

/**
 * 共有ライブラリの入口を出すか。「このプロジェクトへ割り当てられたセクションが
 * 返ってきた」だけで入口を出す（API はアクセス可能なセクションのみ返すため、
 * 返ってきた時点で閲覧権限がある）。ファイル数で判定すると、まだ空のセクションや
 * フォルダしか無いセクションで閲覧のみのメンバーにアイコンが出ず、
 * 割り当て済みのライブラリへ辿れなくなる。
 */
export const canShowLibrary = (
  data: LibraryTreeResponse["data"] | undefined,
): boolean => {
  if (!data) return false;
  const sections = data.sections ?? [];
  const hasLibrarySection = sections.length > 0;
  const canEditLibrary =
    (data.can_manage ?? false) || sections.some((section) => section.can_edit);
  return hasLibrarySection || canEditLibrary;
};

/** ナビアイテムの遷移先 URL（ホームはクエリなし、それ以外は `?tab=`） */
export const projectNavItemHref = (
  projectId: string,
  item: ProjectNavItem,
): string =>
  item.id === "home" ? `/${projectId}` : `/${projectId}?tab=${item.path}`;
